import { Component } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import Fuse from 'fuse.js';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { PageService } from 'src/app/core/page/page.service';
import { RawFormJobObject } from 'src/app/data/dtos';
import { selectColorTheme } from 'src/app/store/color-theme/selectors';
import * as JobsActions from 'src/app/store/jobs/actions';
import {
  errorJobsDataSelector,
  isLoadingJobsDataSelector,
  successJobsDataSelector,
} from 'src/app/store/jobs/selectors';
import { selectActiveLanguage } from 'src/app/store/language/selectors';
import { AppStateInterface } from 'src/app/store/types';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { NgFor, NgClass, AsyncPipe } from '@angular/common';
import { SafeHtmlPipe } from 'src/app/core/custom-pipes/safeHtml.pipe';
import { TranslateModule } from '@ngx-translate/core';
import { JobsSkeletonComponent } from 'src/app/shared/jobs-skeleton/jobs-skeleton.component';
import { BrowserService } from 'src/app/core/browser.service';
import { bootstrapBriefcase, bootstrapBriefcaseFill, bootstrapGeoAltFill, bootstrapGlobe2, bootstrapSearch } from '@ng-icons/bootstrap-icons';
import { Meta, Title } from '@angular/platform-browser';

type searchQuery = {
  jobTitle: string;
  location: string;
};

@Component({
    selector: 'app-jobs',
    templateUrl: './jobs.component.html',
    styleUrls: ['./jobs.component.scss'],
    imports: [
        ReactiveFormsModule,
        RouterModule,
        NgIcon,
        NgFor,
        NgClass,
        JobsSkeletonComponent,
        AsyncPipe,
        SafeHtmlPipe,
        TranslateModule,
    ],
    providers: [
        provideIcons({
            bootstrapBriefcaseFill,
            bootstrapBriefcase,
            bootstrapGeoAltFill,
            bootstrapSearch,
            bootstrapGlobe2
        })
    ]
})
export class JobsComponent {

  private readonly getTitleAndLocationFilter: string = 'fields[0]=title&fields[1]=location'


  isLoading$: Observable<boolean>;
  openJobs$!: Observable<RawFormJobObject[]>;
  totalJobs$!: Observable<number>;
  resultsPerpage$: BehaviorSubject<number> = new BehaviorSubject<number>(20);
  paginationList$: BehaviorSubject<number[]> = new BehaviorSubject<number[]>(
    [],
  );
  currentSelectedPage$: BehaviorSubject<number> = new BehaviorSubject<number>(
    1,
  );
  jobsError$!: Observable<string | null>;
  colorTheme$: BehaviorSubject<string> = new BehaviorSubject(
    this.browserService.localStorageGetItem('color-theme') ?? 'light',
  );

  allAvailableJobTitle: string[]= []
  allAvailableLocations: string[]= []
  selectableJobTitles$: BehaviorSubject<string[] | null> = new BehaviorSubject<string[] | null>(null);
  showSelectableJobTitleOptions: boolean = false
  selectableLocations$: BehaviorSubject<string[] | null> = new BehaviorSubject<string[] | null>(null);
  showSelectableLocationOptions: boolean = false
  jobtitleFuse!: Fuse<string>
  locationFuse!: Fuse<string>


  resultsInGrid: boolean = true;
  readonly pageSize: number = 20;
  private readonly defaultPageStart: number = 1;
  private readonly companyLogo: string = 'company_logo';
  private readonly jobCategories: string = 'job_categories';
  private paginationQueryPart: string = `pagination[page]=${this.defaultPageStart}&pagination[pageSize]=${this.pageSize}&pagination[withCount]=true`;

  activeLanguage: string = '';
  languageDirection: string = 'ltr';

  constructor(
    private _pageService: PageService,
    private _router: Router,
    public activatedRoute: ActivatedRoute,
    private _store: Store<AppStateInterface>,
    private browserService: BrowserService,
    private titleService: Title,
    private metaService: Meta
  ) {
    this.isLoading$ = this._store.select(isLoadingJobsDataSelector);
    this.jobsError$ = this._store.select(errorJobsDataSelector);
    this._store.select(successJobsDataSelector).subscribe({
      next: (result) => {
        this.openJobs$ = of(result.data);
        this.setPaginationData(result.meta.pagination.total);
      },
      error: (error) => {},
    });
  }

  jobForm = new FormGroup({
    jobTitle: new FormControl(),
    location: new FormControl(),
  });

  ngOnInit(): void {
    this._store.select(selectActiveLanguage).subscribe({
      next: (activeLanguage) => {
        this.activeLanguage = activeLanguage;
        this.languageDirection =
          this._pageService.getLanguageDirection(activeLanguage);
        this._pageService.setDirection(activeLanguage);

        this.initSearchableTitleAndLocation(activeLanguage);
        this._store.dispatch(JobsActions.getJobsData({jobsQuery: this.createInitialQuery(activeLanguage)}));
      },
      error: (error) => {},
    });
    this._store.select(selectColorTheme).subscribe({
      next: (colortheme) => {
        this.colorTheme$.next(colortheme);
      },
    });
    this.initSEOContent()
    if (this.browserService.localStorageGetItem('jobs-in-grid') === 'false') {
      this.resultsInGrid = false;
    }
    // this is needed to change direction of jobs received from cms
    this._store.select(successJobsDataSelector).subscribe({
      next: (result) => {
        this.openJobs$ = of(result.data);
      },
      error: (error) => {},
    });

    this.jobForm.get('jobTitle')?.valueChanges.subscribe((query) => {
      if (query) {
        // Perform search and map results
        this.selectableJobTitles$.next(this.jobtitleFuse.search(query).map((result) => result.item));
      } else {
        // If no query, return the original dataset
        this.selectableJobTitles$.next(this.allAvailableJobTitle)
      }
    });

    this.jobForm.get('location')?.valueChanges.subscribe((query) => {
      if (query) {
        // Perform search and map results
        this.selectableLocations$.next(this.locationFuse.search(query).map((result) => result.item));
      } else {
        // If no query, return the original dataset
        this.selectableLocations$.next(this.allAvailableLocations);
      }
    });
  }

  ngAfterViewInit() {}

  onFormSubmit(): void {
    const jobsQuery: string = this.createQuery(this.activeLanguage, '1');
    this._store.dispatch(JobsActions.getJobsData({ jobsQuery: jobsQuery }));
  }

  private createQuery(activeLanguage: string, pageNumber: string): string {
    const paginationQueryPart: string = `&pagination[page]=${pageNumber}&pagination[pageSize]=${this.pageSize}&pagination[withCount]=true`;

    let andIndex: number = 0 // this is used to create the correct and index in the query
    const jobTitleToSearch = this.selectableJobTitles$.getValue() ?? []
    let jobTitleQuery: string = '';
    if(this.jobForm.controls['jobTitle'].value){
      if(jobTitleToSearch.length > 0){
        for(let i = 0; i < jobTitleToSearch.length ; i++){
          jobTitleQuery = jobTitleQuery + `&filters[$and][${andIndex}][$or][${i}][title][$eqi]=${encodeURIComponent(jobTitleToSearch[i])}`
        }
      }
      else {
        jobTitleQuery =`&filters[$and][${andIndex}][title][$eqi]=` + encodeURIComponent(this.jobForm.controls['jobTitle'].value)
      }
      andIndex++
    }
    const locationsToSearch = this.selectableLocations$.getValue() ?? []
    let locationQuery: string = '';
    if(this.jobForm.controls['location'].value){
      if(locationsToSearch.length > 0){
        for(let i = 0; i < locationsToSearch.length ; i++){
          locationQuery = locationQuery + `&filters[$and][${andIndex}][$or][${i}][location][$eqi]=${encodeURIComponent(locationsToSearch[i])}`
        }
      }
      else {
        locationQuery = `&filters[$and][${andIndex}][location][$eqi]=` + encodeURIComponent(this.jobForm.controls['location'].value)
      }
    }
    var jobTitleFormValue = this.jobForm.get('jobTitle')?.value;
    var locationFormValue = this.jobForm.get('location')?.value;
    const searchQueryObject: searchQuery = {
      jobTitle: jobTitleFormValue ?? undefined,
      location: locationFormValue ?? undefined,
    };

    const query = `locale=${activeLanguage}&populate=${this.companyLogo}&populate=${this.jobCategories}${jobTitleQuery}${locationQuery}${paginationQueryPart}`;
    return query;
  }

  showResultsInRows(): void {
    this.resultsInGrid = false;
    this.browserService.localStorageSetItem('jobs-in-grid', 'false');
  }
  showResultsInGrid(): void {
    this.resultsInGrid = true;
    this.browserService.localStorageSetItem('jobs-in-grid', 'true');
  }

  getPage(pageNumber: number): void {
    const currentSelectedPage = this.currentSelectedPage$.value;
    if (pageNumber === currentSelectedPage) {
      return;
    } else if (pageNumber === 0 && currentSelectedPage === 1) {
      return;
    } else if (
      pageNumber === -1 &&
      currentSelectedPage === this.paginationList$.value.length - 2
    ) {
      return;
    }
    if (currentSelectedPage != pageNumber) {
      if (pageNumber === 0) {
        pageNumber = currentSelectedPage - 1;
      } else if (pageNumber === -1) {
        pageNumber = currentSelectedPage + 1;
      }
      let jobsQuery: string = this.createQuery(this.activeLanguage,pageNumber.toString());
      this._store.dispatch(JobsActions.getJobsData({ jobsQuery: jobsQuery }));
      this.currentSelectedPage$.next(pageNumber);
    }
  }

  // navigatetoJob(job: RawFormJobObject): void {
  //   this.browserService.localStorageSetItem('selectedJob', job.attributes.slug);
  //   this._router.navigate(['job/' + job.attributes.slug]);
  // }

  private createInitialQuery(activeLanguage: string): string {
    const emptyQueryWithLanguage = `locale=${activeLanguage}&populate=${this.companyLogo}&populate=${this.jobCategories}&${this.paginationQueryPart}`;
    return emptyQueryWithLanguage;
  }

  private setPaginationData(totalJobs: number): void {
    this.totalJobs$ = of(totalJobs);
    if (totalJobs < this.pageSize) {
      this.resultsPerpage$.next(totalJobs);
    } else {
      this.resultsPerpage$.next(this.pageSize);
    }
    this.createPaginationList(totalJobs);
  }

  private createPaginationList(totalJobs: number) {
    let paginationList: number[] = [];
    if (totalJobs <= this.pageSize) {
      this.paginationList$.next(paginationList);
    } else {
      let pages = Math.ceil(totalJobs / this.pageSize);
      paginationList.push(0);
      for (let i = 1; i < pages + 1; i++) {
        paginationList.push(i);
      }
      paginationList.push(-1);
      this.paginationList$.next(paginationList);
    }
  }


  private initSearchableTitleAndLocation(activeLanguage: string){
    this._pageService.getTitleAndLocation(this.createJobTitleAndLocationQuery(activeLanguage)).subscribe({
      next: result => {
        this.allAvailableJobTitle = Array.from(new Set(result.data.map(p => p.attributes.title)))
        this.allAvailableLocations = Array.from(new Set(result.data.map(p => p.attributes.location)))
        this.selectableJobTitles$.next(this.allAvailableJobTitle);
        this.selectableLocations$.next(this.allAvailableLocations);
        this.jobtitleFuse = this.initFuseJs(this.allAvailableJobTitle, activeLanguage)
        this.locationFuse = this.initFuseJs(this.allAvailableLocations, activeLanguage)
      },
      error: error => {
        console.log(error);
      }
    })
  }

  private initFuseJs(arrayToSearchIn: string[], selectedLanguage: string): Fuse<string>{
    const options = {
      threshold: selectedLanguage === 'fa' ? 0.3 : 0.15,
      includeMatches: true
    };
    return new Fuse<string>(arrayToSearchIn, options);
  }

  private createJobTitleAndLocationQuery(activeLanguage: string){
    return  `?locale=${this.activeLanguage}&${this.getTitleAndLocationFilter}`;;
  }

  onfieldFocused(formControlName: string){
    switch(formControlName){
      case 'jobTitle':
        this.showSelectableJobTitleOptions = true
        break;
      case 'location':
        this.showSelectableLocationOptions = true
        break;
    }
  }

  onFieldBlurd(formControlName: string){
    switch(formControlName){
      case 'jobTitle':
        this.showSelectableJobTitleOptions = false
        break;
      case 'location':
        this.showSelectableLocationOptions = false
        break;
    }
  }

  selectOption(formControlName: string, selectedOption: string){
    switch(formControlName){
      case 'jobTitle':
        this.jobForm.controls['jobTitle'].setValue(selectedOption);
        break;
      case 'location':
        this.jobForm.controls['location'].setValue(selectedOption);
        break;
    }
  }

  public initSEOContent() {
    const urlFragments = this._router.url.split('/');
    if (urlFragments && urlFragments.length > 1 && ['en', 'fa', 'de', 'id'].includes(urlFragments[1])) {
      const language = urlFragments[1];
      this.activatedRoute.data.subscribe(() => {
        // Common meta tags
        const imageUrl = 'https://almanjobs-strapi-public-bucket.s3-eu-central-2.ionoscloud.com/almanjobs_logo_primary_98d1b23517.svg';
        this.metaService.updateTag({ name: 'robots', content: 'index, follow' });
        this.metaService.updateTag({ name: 'viewport', content: 'width=device-width, initial-scale=1.0' });
        this.metaService.updateTag({ name: 'author', content: 'Almanjobs' });
        this.metaService.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
        this.metaService.updateTag({ name: 'og:image', content: imageUrl });
        this.metaService.updateTag({ name: 'twitter:image', content: imageUrl });
        this.metaService.addTag({ rel: 'alternate', hreflang: 'x-default', href: `https://almanjobs.com/en/jobs` });

        // Language-specific meta tags
        if (language === 'en') {
          this.titleService.setTitle('Almanjobs - Search find a Job in Germany | Apply and Migrate with Almanjobs');
          this.metaService.updateTag({
            name: 'description',
            content: 'Search and apply for available jobs in Germany. Explore job opportunities in IT, healthcare, engineering, and more. Secure your future with Almanjobs.',
          });
          this.metaService.updateTag({ name: 'keywords', content: 'job search Germany, work in Germany, apply for jobs, IT jobs, healthcare jobs, German visa, migration services' });
          this.metaService.addTag({ rel: 'alternate', hreflang: 'en', href: `https://almanjobs.com/en/jobs` });
        }

        if (language === 'de') {
          this.titleService.setTitle('Almanjobs - Jobsuche in Deutschland | Bewerben und mit Almanjobs migrieren');
          this.metaService.updateTag({
            name: 'description',
            content: 'Suchen und bewerben Sie sich auf verfügbare Stellen in Deutschland. Entdecken Sie Jobangebote in IT, Gesundheitswesen, Ingenieurwesen und mehr. Sichern Sie Ihre Zukunft mit Almanjobs.',
          });
          this.metaService.updateTag({ name: 'keywords', content: 'Jobsuche Deutschland, Arbeiten in Deutschland, Bewerben um Jobs, IT Jobs, Gesundheitswesen Jobs, deutsches Visum, Migrationsdienste' });
          this.metaService.addTag({ rel: 'alternate', hreflang: 'de', href: `https://almanjobs.com/de/jobs` });
        }

        if (language === 'fa') {
          this.titleService.setTitle('Almanjobs - جستجوی شغل در آلمان | با کمک ما به المان مهاجرت کنید');
          this.metaService.updateTag({
            name: 'description',
            content: 'به دنبال شغل در آلمان بگردید و درخواست دهید. فرصت‌های شغلی در زمینه‌های فناوری اطلاعات، بهداشت و درمان، مهندسی و بیشتر را کشف کنید. آینده خود را با Almanjobs تضمین کنید.',
          });
          this.metaService.updateTag({ name: 'keywords', content: 'جستجوی شغل در آلمان، کار در آلمان، درخواست شغل، شغل‌های IT، شغل‌های بهداشتی، ویزای آلمان، خدمات مهاجرت' });
          this.metaService.addTag({ rel: 'alternate', hreflang: 'fa', href: `https://almanjobs.com/fa/jobs` });
        }
      });
    }
  }

}
